import { CpuChipIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type {
  AssessmentsAccumulationMappingsResponse,
  AssessmentsAccumulationResponse,
} from '@/entities/assessments/api/generated/model';
import type { AmountUnit } from '@/shared/lib/amount/convertAmount';
import { Table, Text } from '@/shared/ui';
import {
  createAIAccumulationsTableColumns,
  STATUS_COLUMN_WIDTH,
} from '../helpers/ai-accumulations-columns';
import { FIRST_COLUMN_WIDTH } from '../helpers/assessment-table-columns';

type AccumulationsTableProps = {
  /** 積算明細データ */
  accumulationData: AssessmentsAccumulationResponse | AssessmentsAccumulationMappingsResponse;
  /** 基準年度 */
  baseFiscalYear: number;
  /** 比較年度 */
  comparedFiscalYear: number;
  /** 金額単位 */
  amountUnit: AmountUnit;
  /** サイドパネル表示かどうか */
  isSidePanelView?: boolean;
};

// 親テーブルの auto 列開始位置（FIRST_COLUMN_WIDTH）に、AI 積算明細テーブルの状態列の右端を揃えるための spacer 幅
const LEFT_SPACER_WIDTH = FIRST_COLUMN_WIDTH - STATUS_COLUMN_WIDTH;

/**
 * 差分用の積算明細テーブルコンポーネント
 */
export const AIAccumulationsTable = ({
  accumulationData,
  baseFiscalYear,
  comparedFiscalYear,
  amountUnit,
  isSidePanelView = false,
}: AccumulationsTableProps) => {
  const { t } = useTranslation();

  const columns = createAIAccumulationsTableColumns({
    baseFiscalYear,
    comparedFiscalYear,
    amountUnit,
    t,
  });

  return (
    <div
      className={isSidePanelView ? undefined : 'grid w-full'}
      style={
        isSidePanelView
          ? undefined
          : {
              gridTemplateColumns: `${LEFT_SPACER_WIDTH}px 1fr`,
            }
      }
    >
      {!isSidePanelView && <div className="border-border-strength1 border-r bg-surface-base" />}
      <Table
        columns={columns}
        data={accumulationData.mappings}
        pagination={null}
        showLastBorderBottom={isSidePanelView}
        emptyRender={
          <div className="flex h-[180px] flex-col items-center justify-center gap-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <CpuChipIcon className="h-6 w-6 text-text-accent" />
            </div>
            <Text variant="body" className="font-medium">
              {t('assessment.messages.ai-empty-title')}
            </Text>
            <Text variant="body" className="text-text-secondary">
              {t('assessment.messages.ai-empty-description')}
            </Text>
          </div>
        }
      />
    </div>
  );
};
